import React from 'react';

import { Alert, Button, Empty, Flex, Spin } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { EpdState } from '@app/models/orders';
import { isForbiddenError } from '@app/services/apiErrors';
import { useEpdOrderTabQuery } from '@app/services/hooks/useEpdOrderTabQuery';
import { AccessDenied } from '@components/AccessDenied';
import { EpdError } from '@components/EpdError';
import { EpdSigningModal } from '@components/EpdSigningModal';
import { ISigningTarget } from '@components/EpdSigningModal/interfaces';
import { EpdStatusTag } from '@components/EpdStatusTag';

import { DocumentCard } from './components/DocumentCard';
import { OrderStages } from './components/OrderStages';

interface IProps {
  orderUid: string;
}

export const EpdTab: React.FC<IProps> = ({ orderUid }) => {
  const epdT = useTranslation(NSKeys.epd);
  const commonT = useTranslation(NSKeys.common);
  const query = useEpdOrderTabQuery(orderUid);
  const [selected, setSelected] = React.useState<ISigningTarget>();
  const awaitingShipment = query.data?.workflow?.epd_state === EpdState.AwaitingShipment;
  if (isForbiddenError(query.error)) return <AccessDenied withProfileLink={false} />;
  if (query.isInitialLoading)
    return (
      <Flex justify="center" style={{ padding: 24 }}>
        <Spin />
      </Flex>
    );
  return (
    <Flex vertical gap={16}>
      {query.isError && (
        <>
          <EpdError error={query.error} />
          <Button onClick={() => query.refetch()}>{commonT.t('buttonReload')}</Button>
        </>
      )}
      {query.data?.feed_stalled && <Alert type="warning" showIcon title={epdT.t('feedStalled')} />}
      {query.data && !query.data.workflow && <Empty description={epdT.t('notMaintained')} />}
      {query.data?.workflow && (
        <>
          <EpdStatusTag state={query.data.workflow.epd_state} />
          <OrderStages workflow={query.data.workflow} documents={query.data.documents} />
          {query.data.documents.map((document) => (
            <DocumentCard
              key={document.uid}
              document={document}
              awaitingShipment={awaitingShipment}
              onSign={setSelected}
            />
          ))}
        </>
      )}
      {selected && <EpdSigningModal targets={[selected]} onClose={() => setSelected(undefined)} />}
    </Flex>
  );
};
